import { ClientProjectActivityListResponseSchema, ClientProjectActivitySchema, MutatedClientProjectActivitySchema, ArchiveClientProjectActivityResponseSchema, ApproveClientProjectActivitiesResponseSchema, AggregateClientProjectActivitiesResponseSchema, } from '@packages/types';
import { api } from '../client.js';
// Multi-value filters emit repeated params (?clientId=a&clientId=b); the
// backend schema accepts a single value or an array for each of these.
function appendAll(params, key, value) {
    if (value === undefined)
        return;
    for (const v of Array.isArray(value) ? value : [value]) {
        if (v)
            params.append(key, v);
    }
}
function appendScope(params, query) {
    appendAll(params, 'status', query?.status);
    if (query?.notInvoiced !== undefined)
        params.set('notInvoiced', String(query.notInvoiced));
    appendAll(params, 'clientId', query?.clientId);
    appendAll(params, 'clientProjectId', query?.clientProjectId);
    if (query?.search)
        params.set('search', query.search);
}
function withQuery(path, params) {
    const qs = params.toString();
    return qs ? `${path}?${qs}` : path;
}
export function listClientProjectActivities(accountId, query) {
    const params = new URLSearchParams();
    appendScope(params, query);
    if (query?.source)
        params.set('source', query.source);
    if (query?.excludeSource)
        params.set('excludeSource', query.excludeSource);
    if (query?.includeArchived)
        params.set('includeArchived', 'true');
    if (query?.isDisbursement !== undefined)
        params.set('isDisbursement', String(query.isDisbursement));
    if (query?.includeUpcoming)
        params.set('includeUpcoming', 'true');
    appendAll(params, 'createdByUserId', query?.createdByUserId);
    appendAll(params, 'performedByUserId', query?.performedByUserId);
    appendAll(params, 'type', query?.type);
    if (query?.sourceRef)
        params.set('sourceRef', query.sourceRef);
    if (query?.sourceEmailMatchId)
        params.set('sourceEmailMatchId', query.sourceEmailMatchId);
    if (query?.sortDirection)
        params.set('sortDirection', query.sortDirection);
    if (query?.cursor)
        params.set('cursor', query.cursor);
    if (query?.limit !== undefined)
        params.set('limit', String(query.limit));
    return api(withQuery(`/accounts/${accountId}/client-project-activities`, params), ClientProjectActivityListResponseSchema);
}
/**
 * Totals over every matching time entry, optionally broken down by one or two
 * dimensions. Takes the list's scope filters, so the two reads agree on which
 * entries are in scope.
 */
export function aggregateClientProjectActivities(accountId, query) {
    const params = new URLSearchParams();
    if (query?.groupBy)
        params.set('groupBy', query.groupBy);
    if (query?.thenBy)
        params.set('thenBy', query.thenBy);
    appendScope(params, query);
    return api(withQuery(`/accounts/${accountId}/client-project-activities/aggregate`, params), AggregateClientProjectActivitiesResponseSchema);
}
export function getClientProjectActivity(accountId, activityId) {
    return api(`/accounts/${accountId}/client-project-activities/${activityId}`, ClientProjectActivitySchema);
}
export function createClientProjectActivity(accountId, input) {
    return api(`/accounts/${accountId}/client-project-activities`, MutatedClientProjectActivitySchema, {
        method: 'POST',
        body: input,
    });
}
export function updateClientProjectActivity(accountId, activityId, input) {
    return api(`/accounts/${accountId}/client-project-activities/${activityId}`, MutatedClientProjectActivitySchema, {
        method: 'PATCH',
        body: input,
    });
}
/** Replace (or, with null, clear) a time entry's file note. Works at every status, INVOICED included. */
export function setClientProjectActivityNote(accountId, activityId, note) {
    return api(`/accounts/${accountId}/client-project-activities/${activityId}/note`, ClientProjectActivitySchema, {
        method: 'PUT',
        body: { note },
    });
}
/** Approve DRAFT time entries together or not at all; pass `expectedUpdatedAt` to refuse if an entry changed since you read it. */
export function approveClientProjectActivities(accountId, input) {
    return api(`/accounts/${accountId}/client-project-activities/approve`, ApproveClientProjectActivitiesResponseSchema, {
        method: 'POST',
        body: input,
    });
}
export function archiveClientProjectActivity(accountId, activityId) {
    return api(`/accounts/${accountId}/client-project-activities/${activityId}`, ArchiveClientProjectActivityResponseSchema, {
        method: 'DELETE',
    });
}
